// Types
import { useEffect, useMemo, useState } from 'react';
// Components
import { Card, CardBody, CardFooter } from 'components/Card';
import { usePrevious } from 'hooks/usePrevious';
import { organizationLoadingSelector } from 'slices/organization.slice';
// Slices
import {
  organizationHierarchyLoading,
  organizationMapSelector,
} from 'slices/organizationHierarchy.slice';
import { rolesMapSelector } from 'slices/roles.slice';
import { useAppSelector } from 'slices/store';
import type { TAttributeValueArrayPair } from '@timeedit/types';
import { Icon } from '@timeedit/ui-components';
import { OrgAndRoleRow } from './OrgAndRoleRow';

type Props = {
  organizationNodesAndRoles: TAttributeValueArrayPair[];
  onChange: (organizationsAndRoles: TAttributeValueArrayPair[]) => void;
  title: string;
  tooltip: string;
  maxRows?: boolean | number;
  minRows?: boolean | number;
  disabled?: boolean;
  key: number | string;
};

type TEIOption = {
  value: string;
  label: string;
  disabled: boolean;
  key: string;
};

type IOrgAndRoleOption = {
  role: string;
} & TEIOption;

export const OrganizationNodesAndRoles = ({
  title,
  tooltip,
  organizationNodesAndRoles,
  onChange,
  key,
}: Props) => {
  const organizationMap = useAppSelector(organizationMapSelector);
  const organizationNodesLoading = useAppSelector(organizationHierarchyLoading);
  const organizationLoading = useAppSelector(organizationLoadingSelector);
  const roleMap = useAppSelector(rolesMapSelector);
  const [sortedAsc, setSortedAsc] = useState(true);
  const [localOrgAndRole, setLocalOrgAndRole] = useState<
    TAttributeValueArrayPair[]
  >([]);

  const prevKey = usePrevious(key);

  useEffect(() => {
    if (prevKey === key) {
      setLocalOrgAndRole(organizationNodesAndRoles);
    }
  }, [organizationNodesAndRoles, prevKey, key]);

  useEffect(() => {
    const sortedOrgAndRole = organizationNodesAndRoles.slice().sort((a, b) => {
      const aLabel = organizationMap[a?.[0]]?.name || '';
      const bLabel = organizationMap[b?.[0]]?.name || '';

      if (sortedAsc) {
        return aLabel.localeCompare(bLabel);
      }
      return bLabel.localeCompare(aLabel);
    });
    setLocalOrgAndRole(sortedOrgAndRole);
    // it's intentional to not include organizationNodesAndRoles in the dependencies,
    // we only want to sort the org and roles when the key changes or user click sort button
  }, [organizationMap, sortedAsc, key]);

  const formattedUserOrgsAndRoles: IOrgAndRoleOption[] = useMemo(() => {
    return localOrgAndRole.map((orgNodeAndRole, index) => ({
      value: orgNodeAndRole[0],
      role: orgNodeAndRole[1],
      label: organizationMap[orgNodeAndRole[0]]?.name || '',
      key: `${index}-${orgNodeAndRole[0]}-${orgNodeAndRole[1]}`,
      disabled: false,
    }));
  }, [localOrgAndRole, organizationMap]);

  const onAddOrgNodeAndRole = () => {
    onChange([...localOrgAndRole, ['', '']]);
  };

  const onUpdateRow = (colIdx: number, rowIdx: number, value: string) => {
    const oldRow = localOrgAndRole[rowIdx];
    const updRow: TAttributeValueArrayPair = [
      colIdx === 0 ? value : oldRow[0],
      colIdx === 1 ? roleMap[value]?.name || '' : oldRow[1],
    ];
    const updatedArr = localOrgAndRole.slice();
    updatedArr.splice(rowIdx, 1, updRow);
    onChange(updatedArr);
  };

  const onDeleteRow = (rowIdx: number) => {
    const updatedArr = localOrgAndRole.slice();
    updatedArr.splice(rowIdx, 1);
    onChange(updatedArr);
  };

  return (
    <Card
      title={
        <div className="org-role-title">
          {title}{' '}
          <div
            className="sort-icon-wrapper"
            onClick={() => setSortedAsc((pre) => !pre)}
          >
            {sortedAsc ? (
              <Icon id="SortAscendingOutlined" alt="Sort Ascending" />
            ) : (
              <Icon id="SortDescendingOutlined" alt="Sort Descending" />
            )}
          </div>
        </div>
      }
      tooltip={tooltip}
      styles={{ body: { paddingTop: '4px' } }}
      footer={
        <CardFooter.AddButton
          onClick={onAddOrgNodeAndRole}
          data-testid="org-node-and-roles-add-button"
          disabled={organizationNodesLoading || organizationLoading}
        />
      }
      resizableContent={(size) => (
        <CardBody.VirtualList
          data={formattedUserOrgsAndRoles}
          maxHeight={size.height}
        >
          {(option, index: number) => (
            <OrgAndRoleRow
              option={option}
              index={index}
              onUpdate={onUpdateRow}
              onDelete={onDeleteRow}
            />
          )}
        </CardBody.VirtualList>
      )}
    />
  );
};
